import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface News {
  id: string;
  title: string;
  content: string | null;
  excerpt: string | null;
  image_url: string | null;
  video_url: string | null;
  external_link: string | null;
  news_type: 'news' | 'event' | 'announcement';
  is_active: boolean;
  is_featured: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  order_index: number;
  // Event specific fields
  event_date: string | null;
  event_location: string | null;
  registration_deadline: string | null;
  registration_link: string | null;
  // Announcement specific fields
  valid_until: string | null;
  is_urgent: boolean | null;
  // Common fields
  tags: string[] | null;
}

export const useNews = (adminMode = false) => {
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const channelRef = useRef<any>(null);
  const maxRetries = 3;

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      const query = supabase.from('news').select('*');
      
      // In admin mode, show all news; otherwise, show only active
      if (!adminMode) {
        query.eq('is_active', true);
      }
      
      const { data, error } = await query
        .order('is_featured', { ascending: false })
        .order('published_at', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNews(data as News[] || []);
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      console.error('Error fetching news:', error);
      if (retryCount < maxRetries) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchNews(), 2000 * retryCount); // Exponential backoff
      } else {
        toast.error('Նորություններ բեռնելու ժամանակ սխալ է տեղի ունեցել');
      }
    } finally {
      setLoading(false);
    }
  }, [adminMode, retryCount]);

  const fetchAllNews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNews(data as News[] || []);
    } catch (error) {
      console.error('Error fetching all news:', error);
      toast.error('Նորություններ բեռնելու ժամանակ սխալ է տեղի ունեցել');
    } finally {
      setLoading(false);
    }
  };

  const createNews = useCallback(async (newsData: Partial<News>) => {
    try {
      // Optimistic update
      const tempId = `temp-${Date.now()}`;
      const optimisticNews = {
        ...newsData,
        id: tempId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as News;
      
      setNews(prev => [optimisticNews, ...prev]);
      
      const user = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('news')
        .insert({
          title: newsData.title || '',
          content: newsData.content,
          excerpt: newsData.excerpt,
          image_url: newsData.image_url,
          video_url: newsData.video_url,
          external_link: newsData.external_link,
          news_type: newsData.news_type || 'news',
          is_active: newsData.is_active ?? true,
          is_featured: newsData.is_featured ?? false,
          published_at: newsData.published_at,
          order_index: newsData.order_index || 0,
          created_by: user.data.user?.id,
          // Event specific fields
          event_date: newsData.event_date || null,
          event_location: newsData.event_location || null,
          registration_deadline: newsData.registration_deadline || null,
          registration_link: newsData.registration_link || null,
          // Announcement specific fields
          valid_until: newsData.valid_until || null,
          is_urgent: newsData.is_urgent ?? false,
          // Common fields
          tags: newsData.tags || []
        })
        .select()
        .single();

      if (error) throw error;
      
      // Replace optimistic update with real data
      setNews(prev => prev.map(item => 
        item.id === tempId ? data as News : item
      ));
      return data;
    } catch (error) {
      console.error('Error creating news:', error);
      // Remove optimistic update on error
      setNews(prev => prev.filter(item => !item.id.startsWith('temp-')));
      throw error;
    }
  }, []);

  const updateNews = useCallback(async (id: string, updates: Partial<News>) => {
    try {
      // Optimistic update
      setNews(prev => prev.map(item => 
        item.id === id ? { ...item, ...updates } as News : item
      ));
      
      const { data, error } = await supabase
        .from('news')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update with server response
      setNews(prev => prev.map(item => item.id === id ? data as News : item));
      return data;
    } catch (error) {
      console.error('Error updating news:', error);
      // Revert optimistic update
      await fetchNews();
      throw error;
    }
  }, [fetchNews]);

  const deleteNews = useCallback(async (id: string) => {
    try {
      // Store deleted item for potential rollback
      const deletedItem = news.find(item => item.id === id);
      
      // Optimistic update
      setNews(prev => prev.filter(item => item.id !== id));
      
      const { error } = await supabase
        .from('news')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting news:', error);
      // Revert optimistic update
      await fetchNews();
      throw error;
    }
  }, [news, fetchNews]);

  // Setup real-time subscriptions
  useEffect(() => {
    fetchNews();
    
    // Setup real-time channel
    const setupRealtimeSubscription = () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }
      
      channelRef.current = supabase
        .channel('news-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'news'
          },
          (payload) => {
            console.log('Real-time INSERT:', payload);
            const newNews = payload.new as News;
            setNews(prev => {
              // Avoid duplicates from optimistic updates
              if (prev.some(item => item.id === newNews.id)) {
                return prev.map(item => item.id === newNews.id ? newNews : item);
              }
              return [newNews, ...prev];
            });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'news'
          },
          (payload) => {
            console.log('Real-time UPDATE:', payload);
            const updatedNews = payload.new as News;
            setNews(prev => prev.map(item => 
              item.id === updatedNews.id ? updatedNews : item
            ));
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'news'
          },
          (payload) => {
            console.log('Real-time DELETE:', payload);
            const deletedId = payload.old.id;
            setNews(prev => prev.filter(item => item.id !== deletedId));
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('Real-time news subscription active');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('Real-time subscription error, retrying...');
            // Retry connection after delay
            setTimeout(setupRealtimeSubscription, 5000);
          }
        });
    };
    
    setupRealtimeSubscription();
    
    // Cleanup
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [fetchNews]);

  return {
    news,
    loading,
    fetchNews,
    fetchAllNews,
    createNews,
    updateNews,
    deleteNews
  };
};